// Email Report Export Service
// Generates CSV/HTML reports and opens email client or downloads file

export interface ReportData {
  title: string;
  period: string;
  generatedAt: string;
  sections: ReportSection[];
}

export interface ReportSection {
  heading: string;
  rows: Array<Record<string, string | number>>;
  headers: string[];
}

export const generateCSV = (data: ReportData): string => {
  let csv = `${data.title}\n`;
  csv += `Period: ${data.period}\n`;
  csv += `Generated: ${data.generatedAt}\n\n`;

  data.sections.forEach((section) => {
    csv += `${section.heading}\n`;
    csv += section.headers.join(',') + '\n';
    section.rows.forEach((row) => {
      csv += section.headers.map((h) => `"${row[h] ?? ''}"`).join(',') + '\n';
    });
    csv += '\n';
  });

  return csv;
};

export const downloadCSV = (data: ReportData, filename?: string): void => {
  let csv = generateCSV(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `${data.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const generateHTMLReport = (data: ReportData): string => {
  const tableStyle = 'border-collapse:collapse;width:100%;margin-bottom:24px;';
  const thStyle = 'background:#1a1a2e;color:white;padding:10px 14px;text-align:left;font-size:13px;';
  const tdStyle = 'padding:10px 14px;border-bottom:1px solid #e5e7eb;font-size:13px;';

  let html = `
    <html><head><meta charset="utf-8"><title>${data.title}</title></head>
    <body style="font-family:Arial,sans-serif;max-width:900px;margin:0 auto;padding:24px;">
    <h1 style="color:#1a1a2e;font-size:24px;margin-bottom:4px;">${data.title}</h1>
    <p style="color:#6b7280;font-size:14px;margin-bottom:8px;">Period: ${data.period}</p>
    <p style="color:#6b7280;font-size:12px;margin-bottom:32px;">Generated: ${data.generatedAt}</p>
  `;

  data.sections.forEach((section) => {
    html += `<h2 style="color:#1a1a2e;font-size:18px;margin-bottom:12px;">${section.heading}</h2>`;
    html += `<table style="${tableStyle}"><thead><tr>`;
    section.headers.forEach((h) => { html += `<th style="${thStyle}">${h}</th>`; });
    html += '</tr></thead><tbody>';
    section.rows.forEach((row, i) => {
      const bg = i % 2 === 0 ? '#f9fafb' : 'white';
      html += `<tr style="background:${bg}">`;
      section.headers.forEach((h) => { html += `<td style="${tdStyle}">${row[h] ?? ''}</td>`; });
      html += '</tr>';
    });
    html += '</tbody></table>';
  });

  html += '</body></html>';
  return html;
};

export const emailReport = (data: ReportData, recipientEmail?: string): void => {
  const subject = encodeURIComponent(`${data.title} - ${data.period}`);
  const body = encodeURIComponent(
    `Please find the ${data.title} report attached.\n\nPeriod: ${data.period}\nGenerated: ${data.generatedAt}\n\nThis report was generated by Dial For Flight Analytics Dashboard.`
  );
  const mailto = `mailto:${recipientEmail || ''}?subject=${subject}&body=${body}`;
  window.open(mailto, '_blank');
};

export const buildBookingReport = (): ReportData => {
  const now = new Date();
  return {
    title: 'Booking Analytics Report',
    period: `${new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('en-GB')} - ${now.toLocaleDateString('en-GB')}`,
    generatedAt: now.toLocaleString('en-GB'),
    sections: [
      {
        heading: 'Booking Summary',
        headers: ['Metric', 'Value', 'Change'],
        rows: [
          { Metric: 'Total Bookings', Value: '247', Change: '+12%' },
          { Metric: 'Confirmed Bookings', Value: '198', Change: '+15%' },
          { Metric: 'Pending Bookings', Value: '32', Change: '-5%' },
          { Metric: 'Cancelled Bookings', Value: '17', Change: '-8%' },
          { Metric: 'Total Revenue', Value: '£68,450', Change: '+18%' },
          { Metric: 'Average Booking Value', Value: '£277', Change: '+6%' },
        ],
      },
      {
        heading: 'Top Routes',
        headers: ['Route', 'Bookings', 'Revenue', 'Avg Price'],
        rows: [
          { Route: 'LHR → DXB', Bookings: 42, Revenue: '£18,900', 'Avg Price': '£450' },
          { Route: 'LHR → IST', Bookings: 38, Revenue: '£11,400', 'Avg Price': '£300' },
          { Route: 'LHR → KHI', Bookings: 35, Revenue: '£14,000', 'Avg Price': '£400' },
          { Route: 'LHR → DAC', Bookings: 28, Revenue: '£9,800', 'Avg Price': '£350' },
          { Route: 'LHR → MLE', Bookings: 22, Revenue: '£17,600', 'Avg Price': '£800' },
        ],
      },
      {
        heading: 'Revenue by Service',
        headers: ['Service', 'Bookings', 'Revenue', 'Share'],
        rows: [
          { Service: 'Flights', Bookings: 142, Revenue: '£38,340', Share: '56%' },
          { Service: 'Holiday Packages', Bookings: 58, Revenue: '£20,300', Share: '30%' },
          { Service: 'Hotels', Bookings: 32, Revenue: '£6,400', Share: '9%' },
          { Service: 'Onward Tickets', Bookings: 15, Revenue: '£3,410', Share: '5%' },
        ],
      },
    ],
  };
};

export const buildLeadReport = (): ReportData => {
  const now = new Date();
  return {
    title: 'Lead Generation Report',
    period: `${new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('en-GB')} - ${now.toLocaleDateString('en-GB')}`,
    generatedAt: now.toLocaleString('en-GB'),
    sections: [
      {
        heading: 'Lead Summary',
        headers: ['Metric', 'Value', 'Change'],
        rows: [
          { Metric: 'Total Leads', Value: '523', Change: '+22%' },
          { Metric: 'Qualified Leads', Value: '312', Change: '+18%' },
          { Metric: 'Converted Leads', Value: '198', Change: '+15%' },
          { Metric: 'Conversion Rate', Value: '37.9%', Change: '+3.2%' },
          { Metric: 'Avg Lead Value', Value: '£345', Change: '+8%' },
        ],
      },
      {
        heading: 'Lead Sources',
        headers: ['Source', 'Leads', 'Conversions', 'Rate'],
        rows: [
          { Source: 'Organic Search', Leads: 198, Conversions: 82, Rate: '41.4%' },
          { Source: 'Direct', Leads: 142, Conversions: 58, Rate: '40.8%' },
          { Source: 'WhatsApp', Leads: 98, Conversions: 42, Rate: '42.9%' },
          { Source: 'Referral', Leads: 52, Conversions: 12, Rate: '23.1%' },
          { Source: 'Social Media', Leads: 33, Conversions: 4, Rate: '12.1%' },
        ],
      },
    ],
  };
};
